import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Chip,
  Avatar,
  Tooltip,
} from "@mui/material";
import { getStatusColor } from "./constants";
import DeliveryDetails from "../Components/DeliveryDetails";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import HistoryIcon from "@mui/icons-material/History";

const getStatusIcon = (status) => {
  const icons = {
    Packing: Inventory2Icon,
    "In Progress": PlayArrowIcon,
    "In Transit": LocalShippingIcon,
    "Ready for Delivery": DeliveryDiningIcon,
    Delivered: CheckCircleIcon,
    Paused: PauseCircleOutlineIcon,
    Canceled: CancelIcon,
  };
  const IconComponent = icons[status] || HistoryIcon;
  return <IconComponent sx={{ fontSize: 20 }} />;
};

const StatusHistorySection = ({ statusHistory, formatDate }) => {
  // Handle empty state
  if (!statusHistory || statusHistory.length === 0) {
    return (
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h5" fontWeight="600" gutterBottom>
            Status History
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              color: "text.secondary",
            }}
          >
            <HistoryIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No status history available
            </Typography>
          </Box>
        </Paper>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            borderColor: "primary.light",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <HistoryIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            Status History
          </Typography>
          <Chip
            label={`${statusHistory.length} ${
              statusHistory.length === 1 ? "update" : "updates"
            }`}
            size="small"
            color="primary"
            sx={{ ml: 1, fontWeight: 600 }}
          />
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Timeline position="alternate" sx={{ p: 0, m: 0 }}>
          {[...statusHistory].map((history, index) => {
            const isLatest = index === 0;
            const statusColor = getStatusColor(history?.status);
            const isLeftSide = index % 2 === 0;

            return (
              <TimelineItem key={index} sx={{ py: 3 }}>
                <TimelineOppositeContent
                  sx={{
                    m: "auto 0",
                    flex: 1,
                    px: 2,
                  }}
                  align={isLeftSide ? "right" : "left"}
                  variant="body2"
                  color="text.secondary"
                >
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: isLatest ? "primary.50" : "grey.50",
                      px: 2,
                      py: 1,
                      borderRadius: "8px",
                      border: `1px solid ${
                        isLatest ? "primary.light" : "grey.300"
                      }`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isLatest ? 700 : 600,
                        color: statusColor,
                      }}
                    >
                      {history?.timestamp
                        ? formatDate(history.timestamp)
                        : "N/A"}
                    </Typography>
                  </Box>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineConnector
                    sx={{
                      backgroundColor: statusColor,
                      width: "3px",
                      minHeight: index === 0 ? "0px" : "50px",
                    }}
                  />
                  <Tooltip
                    title={history?.status || "Unknown"}
                    arrow
                    placement="top"
                  >
                    <TimelineDot
                      sx={{
                        backgroundColor: statusColor,
                        boxShadow: isLatest
                          ? `0 0 0 4px ${statusColor}33`
                          : "none",
                        p: 1.5,
                        border: isLatest
                          ? "3px solid white"
                          : "2px solid white",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.2)",
                          boxShadow: `0 0 0 6px ${statusColor}33`,
                        },
                      }}
                    >
                      {getStatusIcon(history?.status)}
                    </TimelineDot>
                  </Tooltip>
                  <TimelineConnector
                    sx={{
                      backgroundColor: statusColor,
                      width: "3px",
                      minHeight: "50px",
                    }}
                  />
                </TimelineSeparator>
                <TimelineContent sx={{ py: "12px", px: 2, flex: 1 }}>
                  <Paper
                    elevation={isLatest ? 3 : 1}
                    sx={{
                      p: 2.5,
                      background: `linear-gradient(135deg, ${statusColor}15 0%, ${statusColor}05 100%)`,
                      borderLeft: isLeftSide ? `4px solid ${statusColor}` : "",
                      borderRight: isLeftSide ? "" : `4px solid ${statusColor}`,
                      borderRadius: "8px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: isLeftSide ? "flex-start" : "flex-end",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Chip
                        label={history?.status || "Unknown"}
                        size="small"
                        sx={{
                          bgcolor: statusColor,
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                        }}
                      />
                      {isLatest && (
                        <Chip
                          label="Latest"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.primary",
                        fontWeight: isLatest ? 500 : 400,
                        mb:
                          history?.actionBy || history?.status === "Delivered"
                            ? 1.5
                            : 0,
                      }}
                    >
                      {history?.notes || "No notes available"}
                    </Typography>
                    {history?.order_delivery_reason && (
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1,
                          backgroundColor: "rgba(0,0,0,0.03)",
                          borderRadius: "6px",
                          borderLeft: isLeftSide
                            ? `3px solid ${statusColor}`
                            : "",
                          borderRight: isLeftSide
                            ? ""
                            : `3px solid ${statusColor}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Reason
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.primary",
                            fontSize: "0.8125rem",
                            fontWeight: 400,
                            fontStyle: "italic",
                          }}
                        >
                          {history.order_delivery_reason}
                        </Typography>
                      </Box>
                    )}
                    {/* Delivery Details Section */}
                    <DeliveryDetails
                      data={history}
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: "1px solid rgba(0,0,0,0.08)",
                      }}
                    />
                    {history?.actionBy && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: isLeftSide
                            ? "flex-end"
                            : "flex-start",
                          gap: 1,
                          pt: 1.5,
                          mt: 1,
                          borderTop: "1px solid rgba(0,0,0,0.08)",
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            backgroundColor: statusColor,
                            fontSize: "0.875rem",
                            fontWeight: 600,
                          }}
                        >
                          {history.actionBy.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ lineHeight: 1.2 }}
                          >
                            Updated by
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ lineHeight: 1.2 }}
                          >
                            {history.actionBy}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </Paper>
    </Grid>
  );
};

export default StatusHistorySection;
